import {motion, useInView} from 'motion/react';
import {useRef, useState} from 'react';
import {
    Github,
    ExternalLink,
    Code,
    Smartphone,
    BarChart3,
    ShoppingCart,
    Users,
    Cloud,
    GalleryThumbnailsIcon, Images, FilterIcon
} from 'lucide-react';
import {ImageWithFallback} from './figma/ImageWithFallback';
import {useImageGallery} from "./ProjectGallery";

export function Projects() {
    const ref = useRef(null);
    const isInView = useInView(ref, {once: true, amount: 0.1});
    const [filter, setFilter] = useState('All');
    const {GalleryComponent, openGallery} = useImageGallery();

    const projects = [
        {
            id: 1,
            title: 'Umeetly',
            description: 'A professional paid meeting platform enabling users to monetize their skills\n' +
                'through online consultations.',
            images: [
                require("../../src/assets/umeet_3.png"),
                require("../../src/assets/umeet_1.png"),
                require("../../src/assets/umeet_2.png"),
                require("../../src/assets/umeet_4.png"),
                require("../../src/assets/umeet_5.png"),
                require("../../src/assets/umeet_6.png"),
            ],
            tags: ['React', 'Express', 'Typescript', 'Firebase'],
            category: 'Full Stack',
            icon: Code,
            live: 'https://umeet-433222.web.app/',
        },
        {
            id: 2,
            title: 'ReddoPay: Savings and Wallet',
            description: "A digital savings application that enables users to set goals, automate savings, and track financial performance.",
            images: [
                require("../../src/assets/reddopay_1.png"),
                require("../../src/assets/reddopay_2.png"),
                require("../../src/assets/reddopay_3.png"),
                require("../../src/assets/reddopay_4.png"),
                require("../../src/assets/reddopay_5.png"),
                require("../../src/assets/reddopay_6.png"),
            ],
            tags: ['React Native', 'Expo', 'TypeScript', 'Express', 'Firebase', 'Google Cloud'],
            category: 'Mobile',
            icon: Smartphone,
            github: 'https://github.com',
            live: 'https://reddopayapp.com',
        },
        {
            id: 3,
            title: 'Queue: Booking and Queue Management platform',
            description: 'Queue and appointment management system for businesses with an appointment-based operational model (web + mobile)',
            images: [
                require("../../src/assets/queue_4.png"),
                require("../../src/assets/queue_3.png"),
                require("../../src/assets/queue_1.png"),
                require("../../src/assets/queue_2.png"),
                require("../../src/assets/queue_5.png"),
            ],
            tags: ['React', 'Typescript', 'Express', 'Firebase'],
            category: 'Full Stack',
            icon: FilterIcon,
            live: 'https://queue-6db81.web.app/',
        },
        {
            id: 4,
            title: 'Afcog: Africa Connect Group',
            description: 'Corporate presentation website for a global business company.',
            images: [
                require("../../src/assets/afcog_1.png"),
                require("../../src/assets/afcog_2.png"),
                require("../../src/assets/afcog_3.png"),
                require("../../src/assets/afcog_4.png"),
                require("../../src/assets/afcog_5.png"),
                require("../../src/assets/afcog_6.png"),
            ],
            tags: ['React', 'Javascript', 'Express'],
            category: 'Full Stack',
            icon: ShoppingCart,
            github: 'https://github.com',
            live: 'https://afcogs.com',
        },
        {
            id: 5,
            title: 'Reclick Pro',
            description: 'Online course learning platform supporting video content, course progress, and user subscriptions.',
            images: [
                require("../../src/assets/reclick-pro_4.png"),
                require("../../src/assets/reclick-pro_1.png"),
                require("../../src/assets/reclick-pro_2.png"),
                require("../../src/assets/reclick-pro_3.png"),
                require("../../src/assets/reclick-pro_5.png"),
                require("../../src/assets/reclick-pro_6.png"),
            ],
            tags: ['React', 'Typescript', 'Express', 'Firebase'],
            category: 'Full Stack',
            icon: Users,
            github: 'https://github.com',
            live: 'https://reclick-pro.web.app/',
        }, {
            id: 50,
            title: 'Impact Car',
            description: "A carpooling application connecting drivers and passengers for e cient\n" +
                "transportation.",
            images: [
                require("../../src/assets/impactcar_6.jpg"),
                require("../../src/assets/impactcar_1.jpg"),
                require("../../src/assets/impactcar_2.jpg"),
                require("../../src/assets/impactcar_5.jpg"),
                require("../../src/assets/impactcar_3.jpg"),
                require("../../src/assets/impactcar_4.jpg"),
            ],
            tags: ['React', 'Typescript', 'Express', 'Firebase'],
            category: 'Full Stack',
            icon: Users,
            github: 'https://github.com',
            live: 'https://play.google.com/store/apps/details?id=com.icc.impactcar&hl=en',
        },
        {
            id: 6,
            title: 'LinguaScore',
            description: 'A language-test facilitation platform supporting TCF (France & Canada), TOEFL, and IELTS practice and evaluation.',
            images: [
                require("../../src/assets/linguascore_1.png"),
                require("../../src/assets/linguascore_7.png"),
                require("../../src/assets/linguascore_10.png"),
                require("../../src/assets/linguascore_4.png"),
                require("../../src/assets/linguascore_11.png"),
                require("../../src/assets/linguascore_8.png"),
            ],
            tags: ['React', 'Express', 'Firebase', 'Python'],
            category: 'Full Stack',
            icon: Cloud,
            github: 'https://github.com',
            live: 'https://linguascore-c018a.web.app/app',
        },
    ];

    const categories = ['All', 'Full Stack', 'Frontend', 'Backend', 'Mobile'];

    const filteredProjects = filter === 'All'
        ? projects
        : projects.filter(project => project.category === filter);

    return (
        <section id="projects" className="py-32 px-6 relative overflow-hidden" ref={ref}>
            {/* Animated background grid */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                    backgroundImage: 'linear-gradient(rgba(34, 211, 238, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(34, 211, 238, 0.1) 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                }}/>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                <motion.div
                    initial={{opacity: 0, y: 30}}
                    animate={isInView ? {opacity: 1, y: 0} : {}}
                    transition={{duration: 0.6}}
                    className="text-center mb-16"
                >
                    <h2 className="mb-4">
                        Featured <span
                        className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Projects</span>
                    </h2>
                    <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 mx-auto mb-8"/>

                    {/* Filter buttons */}
                    <div className="flex flex-wrap gap-3 justify-center">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setFilter(category)}
                                className={`px-6 py-2 rounded-full transition-all ${
                                    filter === category
                                        ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                                        : 'bg-slate-800/50 text-slate-300 border border-cyan-500/20 hover:border-cyan-500/50'
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredProjects.map((project, index) => (
                        <article itemScope itemType="https://schema.org/SoftwareApplication">
                            <motion.div
                                key={project.id}
                                initial={{opacity: 0, y: 30}}
                                animate={isInView ? {opacity: 1, y: 0} : {}}
                                transition={{duration: 0.6, delay: index * 0.1}}
                                className="group relative"
                            >
                                {/* Glow effect on hover */}
                                <div
                                    className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"/>

                                <div
                                    className="relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl overflow-hidden hover:border-cyan-500/50 transition-all hover:shadow-2xl hover:shadow-cyan-500/20 hover:-translate-y-2">
                                    <div className="relative overflow-hidden aspect-video">
                                        <ImageWithFallback
                                            src={project.images[0]}
                                            alt={project.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div
                                            className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-60"/>

                                        {/* Icon with glow */}
                                        <div
                                            className="absolute top-4 right-4 p-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm border border-cyan-500/30 rounded-xl group-hover:scale-110 transition-transform">
                                            <project.icon className="text-cyan-400" size={24}/>
                                        </div>

                                        {/* Hover overlay with links */}
                                        <div
                                            className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-4">
                                            <button
                                                style={{cursor: "pointer"}}
                                                rel="noopener noreferrer"
                                                className="p-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg hover:bg-white/20 transition-all hover:scale-110"
                                                onClick={(e) => openGallery(project.images.map(img => ({src: img})), 0)}
                                            >
                                                <Images className="text-white" size={24}/>
                                            </button>
                                            <a
                                                href={project.live}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-3 bg-cyan-500/20 backdrop-blur-sm border border-cyan-500/30 rounded-lg hover:bg-cyan-500/30 transition-all hover:scale-110"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <ExternalLink className="text-cyan-400" size={24}/>
                                            </a>
                                        </div>
                                    </div>

                                    <div className="p-6">
                                        <h3 className="text-slate-100 mb-3 group-hover:text-cyan-400 transition-colors"
                                            itemProp="name">{project.title}</h3>
                                        <p className="text-slate-400 text-sm mb-4 line-clamp-2"
                                           itemProp="description">{project.description}</p>

                                        <div className="flex flex-wrap gap-2">
                                            {project.tags.map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-xs hover:bg-cyan-500/20 transition-colors"
                                                >
                        {tag}
                      </span>))}
                                        </div>
                                    </div>
                                </div>

                                <GalleryComponent/>

                            </motion.div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}